import React from 'react';
import { LockIcon } from 'lucide-react';
import { Encounter, Patient } from '../../types';
type AmendFileRecordSummaryProps = {
  patient: Patient;
  encounter: Encounter;
};
const show = (value?: string | number | null) => value === undefined || value === null || String(value).trim() === '' ? 'Not Provided' : String(value);

/**
 * Read-only context of the file being amended: registration, nursing vitals,
 * investigations and prescriptions. Orders and dispensing are not editable
 * here so lab, radiology and pharmacy workflows stay untouched.
 */
export function AmendFileRecordSummary({
  patient,
  encounter
}: AmendFileRecordSummaryProps) {
  const vitals = encounter.vitals;
  return <section aria-labelledby="amend-record-heading" className="rounded-xl border border-gray-200 bg-white" data-id="element-5814" data-mp="u46kb7orw87z">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3" data-id="element-5815" data-mp="2jkfq2v7xz32a">
        <h3 id="amend-record-heading" className="font-semibold text-gray-900" data-id="element-5816" data-mp="2oi7q6ciqpvhk">
          Existing file record
        </h3>
        <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-5817" data-mp="31kyzqx70lfnw">
          <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-5818" data-mp="31iwcum15s2ne" />
          Recorded by reception, nursing, laboratory, radiology & pharmacy —
          read only
        </span>
      </header>

      <div className="grid grid-cols-1 gap-6 px-5 py-4 lg:grid-cols-2" data-id="element-5819" data-mp="3ae2k7pg2uss1">
        <div data-id="element-5820" data-mp="2morju92n30ef">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-5821" data-mp="2mo88jb7hva9d">
            Registration
          </h4>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-id="element-5822" data-mp="2iohcarz781xg">
            {[['Sex', patient.sex], ['Age', patient.age ? `${patient.age} yrs` : ''], ['Contact', patient.contact], ['Marital status', patient.maritalStatus], ['Address', patient.address], ['Next of kin', patient.nextOfKin], ['Allergies', patient.allergies], ['HIV status', patient.hivStatus]].map(([label, value]) => <div key={label} data-id="element-5823" data-mp="iylh3ion48ou">
                <dt className="text-xs text-gray-500" data-id="element-5824" data-mp="ixk1d0woiy0s">{label}</dt>
                <dd className="break-words text-gray-900" data-id="element-5825" data-mp="ny7txr0k3mx4">{show(value)}</dd>
              </div>)}
          </dl>
        </div>

        <div data-id="element-5826" data-mp="svq5lp5ykg8t">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-5827" data-mp="sw9ll0c1suk7">
            Nursing vitals
          </h4>
          {vitals ? <dl className="grid grid-cols-3 gap-x-4 gap-y-1.5 text-sm" data-id="element-5828" data-mp="7lw0sk9nj0u">
              {[['Temp', vitals.temp && `${vitals.temp} °C`], ['Pulse', vitals.pulse], ['Resp', vitals.resp], ['BP', vitals.bp], ['SpO₂', vitals.sp02 && `${vitals.sp02}%`], ['Weight', vitals.weight && `${vitals.weight} kg`], ['Height', vitals.height && `${vitals.height} cm`], ['BMI', vitals.bmi], ['Pain score', vitals.painScore]].map(([label, value]) => <div key={label} data-id="element-5829" data-mp="3144r7lswnzzx">
                  <dt className="text-xs text-gray-500" data-id="element-5830" data-mp="3157fdlyufoc5">{label}</dt>
                  <dd className="text-gray-900" data-id="element-5831" data-mp="2w4s3kyu7mowa">{show(value)}</dd>
                </div>)}
            </dl> : <p className="text-sm italic text-gray-400" data-id="element-5832" data-mp="2r5yqv8mqz1x6">No vitals recorded.</p>}
        </div>

        <div data-id="element-5833" data-mp="2n67p69von1dv">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-5834" data-mp="2n6zitf5pyp1i">
            Investigations
          </h4>
          {encounter.labRequests.length === 0 && encounter.radiologyRequests.length === 0 ? <p className="text-sm italic text-gray-400" data-id="element-5835" data-mp="28gs151njda1v">None requested.</p> : <ul className="space-y-1.5 text-sm" data-id="element-5836" data-mp="23hfg8mg86d1x">
              {encounter.labRequests.map(lab => <li key={lab.id} className="flex justify-between gap-3" data-id="element-5837" data-mp="1zeslods66e11">
                  <span className="text-gray-900" data-id="element-5838" data-mp="2o5ygz62w054s">
                    Lab: {[...lab.tests, lab.otherTests].filter(Boolean).join(', ')}
                  </span>
                  <span className="shrink-0 text-xs text-gray-500" data-id="element-5839" data-mp="2t4jkpfhcl6xz">{lab.status}</span>
                </li>)}
              {encounter.radiologyRequests.map(rad => <li key={rad.id} className="flex justify-between gap-3" data-id="element-5840" data-mp="2je6qswnn81f6">
                  <span className="text-gray-900" data-id="element-5841" data-mp="25exlwt4w0tqi">Radiology: {rad.scanType}</span>
                  <span className="shrink-0 text-xs text-gray-500" data-id="element-5842" data-mp="2aejzj3omobzu">{rad.status}</span>
                </li>)}
            </ul>}
        </div>

        <div data-id="element-5843" data-mp="1wgdbef9hdqdd">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-5844" data-mp="1wgvf1wyh36vb">
            Prescriptions
          </h4>
          {encounter.prescriptions.length === 0 ? <p className="text-sm italic text-gray-400" data-id="element-5845" data-mp="3qtl686xgi1z0">None prescribed.</p> : <ul className="space-y-1.5 text-sm" data-id="element-5846" data-mp="3utvejq8q14h1">
              {encounter.prescriptions.map(rx => <li key={rx.id} className="flex justify-between gap-3" data-id="element-5847" data-mp="1j7bdfu1zyw2q">
                  <span className="text-gray-900" data-id="element-5848" data-mp="1f6hnyw8wp97q">
                    {rx.drugName} {rx.dose} · {rx.route} · {rx.frequency} ·{' '}
                    {rx.days} days
                  </span>
                  <span className="shrink-0 text-xs text-gray-500" data-id="element-5849" data-mp="1o5wgs2cjpvve">{rx.status}</span>
                </li>)}
            </ul>}
        </div>
      </div>
    </section>;
}